import './styles/main.css';
import { initDB } from './store/db';
import { renderCalendar } from './components/Calendar';
import { habitStore } from './store/habitStore';
import { renderHabitModal } from './components/AddHabitModal';
import { renderHabitCard } from './components/HabitCard';
import { renderDashboard } from './components/Dashboard';
import { NotificationManager } from './utils/NotificationManager';
import { PhotoStorage } from './utils/PhotoStorage';
import { AuthManager } from './utils/AuthManager';
import { HealthSyncManager } from './utils/HealthSyncManager';

let currentViewDate = new Date();
let currentScene = 'daily'; // 'daily' or 'stats'
let deferredPrompt = null;

async function initApp() {
  const loginResult = AuthManager.init(); // Check if we just returned from OAuth redirect

  if (loginResult === 'migration') {
    // Just logged in, trigger migration of local anonymous data
    setTimeout(() => habitStore.markAllAsDirty(), 1000);
  } else if (loginResult === 'switch') {
    // User switched accounts, clear local data and re-sync
    await habitStore.clearLocalData();
    setTimeout(() => habitStore.syncWithRemote(), 500);
  }

  if (!localStorage.getItem('df_user_id')) {
    localStorage.setItem('df_user_id', crypto.randomUUID());
  }

  try {
    await initDB();

    const app = document.getElementById('app');
    app.innerHTML = `
      <div id="offline-indicator" class="hidden bg-df-sapphire text-white text-[10px] py-1 text-center font-black tracking-widest animate-in slide-in-from-top duration-500 z-[200] relative">
        OFFLINE MODE — LOCAL STORAGE ACTIVE
      </div>
      <input type="file" id="photo-input" class="hidden" accept="image/*" />
      <div id="modal-root"></div>
      <div class="p-6 max-w-md mx-auto bg-white min-h-screen flex flex-col pb-40">
        <header class="mb-10 flex justify-between items-start">
          <div>
            <h1 class="text-4xl font-black text-df-sapphire tracking-tighter italic">Daily Fit</h1>
            <p class="text-df-bluegrey mt-1 font-bold text-sm tracking-wide opacity-60">Consistency is power.</p>
          </div>
          <div class="flex flex-col items-end gap-3 min-w-[120px]">
            <div id="sync-pill" class="px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-[0.1em] transition-all duration-300 bg-gray-100 text-gray-400">
                Idle
            </div>
            <div class="flex items-center gap-2">
                <button id="btn-notify" class="w-8 h-8 flex items-center justify-center bg-gray-50 rounded-full text-df-bluegrey hover:bg-df-sapphire/10 transition-all active:scale-90 shadow-sm border border-gray-100">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                </button>
                <div id="auth-container" class="flex items-center"></div>
            </div>
          </div>
        </header>

        <main id="main-content" class="flex-1 flex flex-col">
            <!-- Scene content renders here -->
        </main>
        
        <div id="install-banner" class="hidden mt-8 p-6 bg-df-sapphire rounded-[2rem] text-white flex flex-col items-center text-center animate-in slide-in-from-bottom duration-700">
            <h4 class="font-black text-lg mb-2">Install Daily Fit</h4>
            <p class="text-xs font-bold opacity-80 mb-6">Add to your home screen for the best experience.</p>
            <button id="btn-install" class="w-full py-4 bg-white text-df-sapphire rounded-2xl font-black text-sm uppercase tracking-widest shadow-lg">Install Now</button>
        </div>

        <nav class="fixed bottom-8 left-1/2 -translate-x-1/2 w-[calc(100%-3rem)] max-w-sm bg-df-sapphire/95 backdrop-blur-xl p-2 rounded-[2.5rem] flex items-center justify-between shadow-2xl shadow-df-sapphire/40 border border-white/10 z-[100]">
            <button id="nav-daily" class="flex-1 py-4 flex flex-col items-center gap-1 group transition-all duration-300">
                <div class="nav-icon-bg w-12 h-1 rounded-full bg-white opacity-0 group-[.active]:opacity-100 mb-1 transition-all"></div>
                <span class="text-[10px] font-black uppercase tracking-widest text-white/40 group-[.active]:text-white">Daily</span>
            </button>
            <button id="nav-stats" class="flex-1 py-4 flex flex-col items-center gap-1 group transition-all duration-300">
                <div class="nav-icon-bg w-12 h-1 rounded-full bg-white opacity-0 group-[.active]:opacity-100 mb-1 transition-all"></div>
                <span class="text-[10px] font-black uppercase tracking-widest text-white/40 group-[.active]:text-white">Stats</span>
            </button>
        </nav>
      </div>
    `;

    // Global Sync UI listeners
    window.addEventListener('syncStatusChange', (e) => {
      const pill = document.getElementById('sync-pill');
      if (!pill) return;

      switch (e.detail) {
        case 'syncing':
          pill.className = 'px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-[0.1em] transition-all duration-300 bg-df-sapphire/10 text-df-sapphire animate-pulse';
          pill.textContent = 'Syncing...';
          break;
        case 'synced':
          pill.className = 'px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-[0.1em] transition-all duration-300 bg-df-aqua/10 text-df-aqua';
          pill.textContent = 'Synced';
          setTimeout(() => {
            if (pill.textContent === 'Synced') {
              pill.textContent = 'Idle';
              pill.className = 'px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-[0.1em] transition-all duration-300 bg-gray-100 text-gray-400';
            }
          }, 4000);
          renderUI();
          break;
        case 'offline':
          pill.className = 'px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-[0.1em] transition-all duration-300 bg-gray-100 text-gray-400';
          pill.textContent = 'Offline';
          break;
        case 'error':
          pill.className = 'px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-[0.1em] transition-all duration-300 bg-red-100 text-red-500';
          pill.textContent = 'Error';
          break;
      }
    });

    renderUI();
    renderAuth();

    document.getElementById('nav-daily').onclick = () => {
      currentScene = 'daily';
      renderUI();
    };
    document.getElementById('nav-stats').onclick = () => {
      currentScene = 'stats';
      renderUI();
    };

    document.getElementById('btn-notify').onclick = async () => {
      const granted = await NotificationManager.requestPermission();
      if (granted) {
        alert('Notifications enabled! You will now receive daily reminders.');
        await NotificationManager.sendTestNotification();
      } else {
        alert('Please enable notifications in your browser settings to receive reminders.');
      }
    };

    const photoInput = document.getElementById('photo-input');
    photoInput.onchange = async (e) => {
      const file = e.target.files[0];
      const logId = photoInput.dataset.logId;
      if (file && logId) {
        await PhotoStorage.savePhoto(parseInt(logId), file);
        renderUI();
      }
    };

    habitStore.syncWithRemote().catch(() => { });

    window.addEventListener('online', () => habitStore.syncWithRemote());
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        habitStore.syncWithRemote();
        HealthSyncManager.syncAll().catch(() => { });
      }
    });
    setInterval(() => habitStore.syncWithRemote(), 5 * 60 * 1000);

    // Initial Health Sync
    HealthSyncManager.syncAll().catch(() => { });

    updateOnlineStatus();
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    // Install Prompt Logic
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      deferredPrompt = e;
      document.getElementById('install-banner').classList.remove('hidden');
    });

    document.getElementById('btn-install').onclick = async () => {
      if (!deferredPrompt) return;
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        document.getElementById('install-banner').classList.add('hidden');
      }
      deferredPrompt = null;
    };

  } catch (error) {
    // Silently handle in production
  }
}

async function renderUI() {
  const mainContent = document.getElementById('main-content');
  if (!mainContent) return;

  // Update Nav selection
  document.getElementById('nav-daily').classList.toggle('active', currentScene === 'daily');
  document.getElementById('nav-stats').classList.toggle('active', currentScene === 'stats');

  // Enforce Social Login Check
  const isLoggedIn = AuthManager.isLoggedIn();
  const nav = document.querySelector('nav');

  if (!isLoggedIn) {
    if (nav) nav.classList.add('hidden');
    mainContent.innerHTML = `
        <div class="flex-1 flex flex-col items-center justify-center text-center py-20 animate-in fade-in zoom-in duration-700">
            <div class="w-20 h-20 bg-df-sapphire/5 rounded-[2.5rem] flex items-center justify-center mb-8">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-df-sapphire" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
            </div>
            <h2 class="text-3xl font-black text-df-sapphire tracking-tighter mb-4">Daily Fit에 오신 것을 환영합니다!</h2>
            <p class="text-df-bluegrey font-bold text-sm tracking-wide opacity-60 mb-10 max-w-[240px]">데이터를 안전하게 보관하고 앱을 사용하시려면 소셜 로그인이 필요합니다.</p>
            
            <div class="flex flex-col gap-4 w-full px-10">
                <button id="login-google-big" class="flex items-center justify-center gap-3 w-full py-4 bg-white border border-gray-100 shadow-xl shadow-gray-200/50 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-gray-50 transition-all active:scale-95">
                    <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" class="w-5 h-5" />
                    Google로 시작하기
                </button>
                <button id="login-kakao-big" class="flex items-center justify-center gap-3 w-full py-4 bg-[#FEE500] rounded-2xl font-black text-sm uppercase tracking-widest hover:opacity-90 shadow-xl shadow-[#FEE500]/20 transition-all active:scale-95 text-[#3C1E1E]">
                    <svg width="20" height="19" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 0C4.029 0 0 3.123 0 6.975C0 9.471 1.62 11.691 4.05 12.915L3.024 16.668C2.952 16.929 3.249 17.154 3.474 17.001L7.902 14.049C8.262 14.076 8.622 14.1 9 14.1C13.971 14.1 18 10.977 18 7.125C18 3.273 13.971 0.15 9 0.15" fill="#3C1E1E"/>
                    </svg>
                    Kakao로 시작하기
                </button>
            </div>
            
            <p class="mt-12 text-[10px] text-slate-400 font-bold uppercase tracking-widest px-8">
                데이터는 계정별로 별도 관리되며 서로 섞이지 않습니다.
            </p>
        </div>
      `;
    document.getElementById('login-google-big').onclick = () => AuthManager.login('google');
    document.getElementById('login-kakao-big').onclick = () => AuthManager.login('kakao');
    return;
  }

  if (nav) nav.classList.remove('hidden');

  if (currentScene === 'stats') {
    mainContent.innerHTML = '<div id="stats-container"></div>';
    await renderDashboard('stats-container');
    if (window.gtag) {
      gtag('event', 'page_view', {
        page_title: 'Stats Dashboard',
        page_location: window.location.href,
        page_path: '/stats'
      });
    }
    return;
  }

  // Daily Scene
  if (window.gtag) {
    gtag('event', 'page_view', {
      page_title: 'Daily Habits',
      page_location: window.location.href,
      page_path: '/daily'
    });
  }
  mainContent.innerHTML = `
    <section id="calendar-container" class="mb-12 bg-gray-50/50 p-5 rounded-[2.5rem] border border-gray-100/50 shadow-sm backdrop-blur-sm">
      <!-- Calendar renders here -->
    </section>

    <section id="habits-list" class="space-y-6 flex-1">
      <div class="flex justify-between items-end mb-4 px-2">
        <h3 class="text-2xl font-black text-df-sapphire">Daily Habits</h3>
        <button id="btn-add-habit" class="bg-df-aqua text-white px-6 py-3 rounded-2xl text-xs font-black shadow-xl shadow-df-aqua/20 hover:scale-105 active:scale-95 transition-all uppercase tracking-widest">
          + New
        </button>
      </div>
      <div id="habits-content" class="space-y-4">
        <!-- Habits render here -->
      </div>
    </section>
  `;

  document.getElementById('btn-add-habit').onclick = () => {
    renderHabitModal(async (habit) => {
      await habitStore.addHabit(habit);
      renderUI();
    }, () => { }, () => { });
  };

  const allHabits = await habitStore.getAllHabits();
  const dateStr = currentViewDate.toISOString().split('T')[0];
  const dayOfWeek = currentViewDate.getDay();

  const habits = allHabits.filter(h => {
    if (h.is_deleted) return false;
    if (h.start_date && dateStr < h.start_date) return false;
    if (h.end_date && dateStr > h.end_date) return false;
    if (h.repeat_days) {
      const days = h.repeat_days.split(',').map(d => parseInt(d));
      if (!days.includes(dayOfWeek)) return false;
    }
    return true;
  });

  const logs = await habitStore.getLogsByDate(dateStr);
  const logMap = new Map(logs.map(l => [l.habitId, l]));
  const rates = await habitStore.getDailyCompletionRates();

  const photoMap = new Map();
  for (const log of logs) {
    const photo = await PhotoStorage.getPhotoByLog(log.id);
    if (photo) {
      photoMap.set(log.id, URL.createObjectURL(photo.blob));
    }
  }

  renderCalendar('calendar-container', currentViewDate, rates, {
    onPrevWeek: () => { currentViewDate.setDate(currentViewDate.getDate() - 7); renderUI(); },
    onNextWeek: () => { currentViewDate.setDate(currentViewDate.getDate() + 7); renderUI(); },
    onDateSelect: (date) => { currentViewDate = date; renderUI(); }
  });

  const content = document.getElementById('habits-content');
  if (habits.length === 0) {
    content.innerHTML = `
        <div class="py-20 flex flex-col items-center text-center space-y-6">
            <div class="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center animate-bounce duration-[2000ms]">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-df-bluegrey opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            </div>
            <div>
                <h4 class="text-df-sapphire font-black text-lg">Nothing scheduled</h4>
                <p class="text-sm font-bold text-df-bluegrey opacity-60 mt-2 px-10 leading-relaxed">Relax! There are no habits set for<br>${currentViewDate.toLocaleDateString(undefined, { month: 'long', day: 'numeric', weekday: 'long' })}.</p>
            </div>
        </div>
    `;
    return;
  }

  content.innerHTML = habits.map(h => {
    const log = logMap.get(h.id);
    const photoUrl = log ? photoMap.get(log.id) : null;
    return renderHabitCard(h, log, photoUrl);
  }).join('');

  content.querySelectorAll('.btn-add-photo').forEach(btn => {
    btn.onclick = (e) => {
      e.stopPropagation();
      const logId = btn.dataset.logId;
      const photoInput = document.getElementById('photo-input');
      photoInput.dataset.logId = logId;
      photoInput.click();
    };
  });

  content.querySelectorAll('.habit-item').forEach(item => {
    item.onclick = async (e) => {
      const habitId = item.dataset.habitId;
      const habit = habits.find(h => h.id === habitId);
      const log = logMap.get(habitId);

      if (habit.type === 'binary') {
        const newStatus = !(log?.is_completed);
        await habitStore.updateLog(habitId, dateStr, null, newStatus);
      } else {
        const newValue = prompt(`Progress for ${habit.title} (${habit.unit}):`, log?.value || 0);
        if (newValue !== null && newValue !== "") {
          const val = parseFloat(newValue);
          const isCompleted = habit.direction === 'decrease' ? val <= habit.goal : val >= habit.goal;
          await habitStore.updateLog(habitId, dateStr, val, isCompleted);
        }
      }
      renderUI();
    };
  });

  content.querySelectorAll('.btn-edit-habit').forEach(btn => {
    btn.onclick = async (e) => {
      e.stopPropagation();
      const habitId = btn.dataset.habitId;
      const habit = allHabits.find(h => h.id === habitId);

      renderHabitModal(
        async (updates) => {
          await habitStore.updateHabit(habitId, updates);
          renderUI();
        },
        () => { }, // onCancel
        async (id) => {
          await habitStore.deleteHabit(id);
          renderUI();
        },
        habit
      );
    };
  });
}

function renderAuth() {
  const container = document.getElementById('auth-container');
  if (!container) return;

  if (AuthManager.isLoggedIn()) {
    container.innerHTML = `
            <div class="flex gap-2 items-center">
                <button id="btn-reset" class="px-2 py-1 text-[10px] font-bold text-red-300 hover:text-red-500 transition-all uppercase tracking-tighter" title="Reset all data">
                    Reset
                </button>
                <div class="w-px h-3 bg-gray-200"></div>
                <button id="btn-logout" class="px-3 py-1 bg-gray-50 rounded-full text-[10px] font-black uppercase text-df-bluegrey border border-gray-100 shadow-sm hover:bg-red-50 hover:text-red-500 transition-all">
                    Out
                </button>
            </div>
        `;
    document.getElementById('btn-logout').onclick = () => {
      if (confirm('로그아웃 하시겠습니까?\n(기기의 로컬 데이터는 유지되지만, 다른 계정으로 로그인 시 삭제될 수 있습니다.)')) {
        AuthManager.logout();
      }
    };
    document.getElementById('btn-reset').onclick = async () => {
      if (confirm('⚠️ 모든 데이터를 초기화하시겠습니까?\n이 계정의 서버 데이터와 기기의 모든 기록이 삭제되며 복구할 수 없습니다!')) {
        try {
          await habitStore.resetAllData();
          alert('모든 데이터가 삭제되었습니다. 처음부터 다시 시작합니다!');
          window.location.reload();
        } catch (e) {
          alert('초기화에 실패했습니다. 네트워크 연결을 확인해 주세요.');
        }
      }
    };
  } else {
    container.innerHTML = ''; // Hide auth in header when logged out since it's now a big splash screen
  }
}

function updateOnlineStatus() {
  const indicator = document.getElementById('offline-indicator');
  if (navigator.onLine) {
    indicator?.classList.add('hidden');
  } else {
    indicator?.classList.remove('hidden');
  }
}

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { });
  });
}

document.addEventListener('DOMContentLoaded', initApp);
